/* global globalThis */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../../../services/api';

// Fetches /projects/{projectId}/gantt/flat and returns raw + mapped rows for Google Gantt
export function useGanttFlat() {
const { projectId } = useParams();
const [raw, setRaw] = useState([]);
const [loading, setLoading] = useState(true);
const [error, setError] = useState(null);
let fallbackTaskCounter = 0;

const generateTaskFallbackId = () => {
  if (typeof globalThis !== 'undefined' && globalThis.crypto) {
    if (typeof globalThis.crypto.randomUUID === 'function') {
      return globalThis.crypto.randomUUID();
    }
    if (typeof globalThis.crypto.getRandomValues === 'function') {
      const randomBytes = new Uint8Array(16);
      globalThis.crypto.getRandomValues(randomBytes);
      return Array.from(randomBytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    }
  }

  fallbackTaskCounter += 1;
  return `task-${Date.now()}-${fallbackTaskCounter}`;
};

const romanToInt = (roman = '') => {
  const vals = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };
  let sum = 0;
  let prev = 0;
  for (let i = roman.length - 1; i >= 0; i -= 1) {
    const val = vals[roman[i]] || 0;
    if (val < prev) sum -= val;
    else sum += val;
    prev = val;
  }
  return sum;
};

const extractPrefix = (name = '') => {
  const match = name.trim().match(/^([IVXLCDM]+)\.?\s*([0-9]+)?/i);
  const roman = match?.[1]?.toUpperCase() || null;
  return {
    romanValue: roman ? romanToInt(roman) : null,
    numberValue: match?.[2] !== undefined ? parseInt(match[2], 10) : null,
  };
};

// Sort by roman numeral + numeric code (e.g., "III.02 ..."), fallback to alpha
const sortByStandardPrefix = (a, b) => {
  const nameA = (a?.text ?? a?.name ?? '').trim();
  const nameB = (b?.text ?? b?.name ?? '').trim();
  const aParts = extractPrefix(nameA);
  const bParts = extractPrefix(nameB);

  if (aParts.romanValue !== null && bParts.romanValue !== null) {
    if (aParts.romanValue !== bParts.romanValue) {
      return aParts.romanValue - bParts.romanValue;
    }
    if (aParts.numberValue !== null && bParts.numberValue !== null && aParts.numberValue !== bParts.numberValue) {
      return aParts.numberValue - bParts.numberValue;
    }
    if (aParts.numberValue !== null && bParts.numberValue === null) return 1;
    if (aParts.numberValue === null && bParts.numberValue !== null) return -1;
  } else if (aParts.romanValue !== null || bParts.romanValue !== null) {
    return aParts.romanValue !== null ? -1 : 1;
  }

  return nameA.localeCompare(nameB);
};

// Keep hierarchical order but sort siblings by the standardized prefix
const sortFlatWithHierarchy = (list = []) => {
  const byParent = new Map();
  const visited = new Set();
  const getPid = (n) => {
    const p = n?.parent ?? n?.parentId ?? n?.parent_id ?? 0;
    return String(p || p === 0 ? p : 0);
  };

  list.forEach((n) => {
    const pid = getPid(n);
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid).push(n);
  });

  const ordered = [];
  const walk = (pid) => {
    const children = byParent.get(pid) || [];
    children.sort(sortByStandardPrefix);
    children.forEach((child) => {
      ordered.push(child);
      visited.add(child);
      walk(String(child.id));
    });
  };

  walk('0');

  // Append orphans (items whose parents are missing)
  list.forEach((n) => {
    if (!visited.has(n)) ordered.push(n);
  });

  return ordered;
};

  const fetchFlat = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/projects/${projectId}/gantt/flat`);
      const payload = res?.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.data?.data)
        ? payload.data.data
        : Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(payload?.result)
        ? payload.result
        : Array.isArray(payload?.tasks)
        ? payload.tasks
        : [];
      setRaw(sortFlatWithHierarchy(list));
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchFlat(); }, [fetchFlat]);

  // Map to Google Gantt rows
  const chartRows = useMemo(() => {
    // Columns: [id, name, resource, start, end, duration(ms), percent(0-100), deps]
    const parseDate = (v) => {
      if (!v) return null;
      const d = v instanceof Date ? v : new Date(v);
      return isNaN(d.getTime()) ? null : d;
    };

    const toMs = (daysLike) => {
      if (daysLike == null) return null;
      const n = Number(daysLike);
      if (!isFinite(n)) return null;
      // Heuristic: values > 1000 likely already ms
      return n > 1000 ? n : Math.max(1, Math.round(n * 24 * 60 * 60 * 1000));
    };

    const items = Array.isArray(raw) ? raw : [];

    // Build lookup and children for computing summaries
    const byId = new Map();
    items.forEach((it) => byId.set(String(it.id), it));
    const children = new Map();
    items.forEach((it) => {
      const pid = it.parent;
      if (pid && pid !== 0) {
        const key = String(pid);
        if (!children.has(key)) children.set(key, []);
        children.get(key).push(it);
      }
    });

    // Helpers to compute min/max date among descendants
    const getDate = (v) => (v ? new Date(v) : null);
    const minMax = (nodes) => {
      let min = null, max = null;
      nodes.forEach((n) => {
        const s = getDate(n.start_date);
        const e = getDate(n.end_date);
        if (s && (!min || s < min)) min = s;
        if (e && (!max || e > max)) max = e;
      });
      return { min, max };
    };

    // Create synthetic summary items for folders (project/modularUnit/workCategory)
    const synthetic = [];
    items.forEach((n) => {
      const isFolder = n.isFolder || n.type === 'project' || n.type === 'modularUnit' || n.type === 'workCategory';
      if (!isFolder) return;
      // compute span from immediate task descendants under this subtree
      const stack = [...(children.get(String(n.id)) || [])];
      const collect = [];
      while (stack.length) {
        const cur = stack.pop();
        if (!cur) continue;
        if (cur.type === 'task') collect.push(cur);
        const kids = children.get(String(cur.id));
        if (kids?.length) stack.push(...kids);
      }
      const { min, max } = minMax(collect);
      if (min && max) {
        synthetic.push({
          id: `summary_${n.id}`,
          text: n.text || n.name,
          type: 'summary',
          start_date: min.toISOString(),
          end_date: max.toISOString(),
          duration: null,
          progress: 0,
          parent: n.parent,
        });
      }
    });

    // Prepare task rows first
    const taskRows = items
      .filter((t) => (t.type ? t.type === 'task' : true))
      .map((t) => {
        const id = String(t.id ?? t._id ?? t.taskId ?? t.code ?? generateTaskFallbackId());
        const name = t.text ?? t.name ?? t.title ?? t.taskName ?? `Task ${id}`;
        // Show subcontractor name if present; else show Team ID like screenshot
        const resource = t.subcontractorName ?? (t.assignedTo != null ? `Team ID: ${t.assignedTo}` : (t.subcontractor ?? t.resource ?? ''));
        const startRaw = t.start_date ?? t.startDate ?? t.start ?? t.plannedStart ?? t.StartDate;
        const endRaw = t.end_date ?? t.endDate ?? t.finish ?? t.plannedEnd ?? t.EndDate;
        let start = parseDate(startRaw);
        let end = parseDate(endRaw);
        let duration = null;

      // Try explicit duration fields first (days or ms)
      duration = toMs(
        t.durationDays ?? t.duration_day ?? t.duration ?? t.days ?? t.plannedDuration
      );
      if (start && end) {
        // Use start+end; ignore duration so Google computes it
        duration = null;
      } else {
        // Ensure we have a valid start and a positive duration
        if (!duration) duration = toMs(1); // default 1 day
        if (!start && end) {
          start = new Date(end.getTime() - duration);
        }
        if (!start && !end) {
          start = new Date();
          end = null; // Google will use duration from start
        }
      }

      let percent = Number(
        t.progressPct ?? t.progressPercent ?? t.progress ?? 0
      );
      // API returns 0..1 — convert to 0..100
      if (percent <= 1) percent = Math.round(percent * 100);

      const depsArr = Array.isArray(t.dependencies)
        ? t.dependencies.map((d) => String(d.taskId ?? d.id ?? d))
        : Array.isArray(t.predecessors)
        ? t.predecessors.map(String)
        : [];
      return [id, name, resource, start, end, duration, percent, depsArr];
    });

    // Build dependency graph and remove edges that create cycles or point to missing ids
    const idSet = new Set(taskRows.map((r) => r[0]));
    const adj = new Map();
    taskRows.forEach((r) => adj.set(r[0], new Set()));
    const hasCycleIfAdd = (from, to) => {
      // DFS from 'to' to see if we can reach 'from'
      const seen = new Set();
      const stack = [to];
      while (stack.length) {
        const cur = stack.pop();
        if (cur === from) return true;
        if (seen.has(cur)) continue;
        seen.add(cur);
        const next = adj.get(cur);
        if (next) next.forEach((n) => stack.push(n));
      }
      return false;
    };

    // sanitize deps
    const sanitizedRows = taskRows.map((r) => {
      const id = r[0];
      const deps = Array.isArray(r[7]) ? r[7] : [];
      const kept = [];
      deps.forEach((to) => {
        if (!idSet.has(to) || to === id) return; // drop unknown/self
        if (hasCycleIfAdd(id, to)) return; // would create cycle
        adj.get(id).add(to);
        kept.push(to);
      });
      // replace deps with CSV string for Google
      return [r[0], r[1], r[2], r[3], r[4], r[5], r[6], kept.join(',') || null];
    });

    // Convert synthetic summaries into rows (no deps)
    const summaryRows = synthetic.map((t) => {
      const id = String(t.id);
      const name = t.text ?? `Group ${id}`;
      const resource = '';
      const start = parseDate(t.start_date);
      const end = parseDate(t.end_date);
      const duration = null;
      const percent = 0;
      const deps = null;
      return [id, name, resource, start, end, duration, percent, deps];
    });

    return [...summaryRows, ...sanitizedRows];
  }, [raw]);

  return { raw, chartRows, loading, error, refresh: fetchFlat };
}
